import { useMemo, useState } from 'react';
import { Shield, Info, Plus, MoreHorizontal } from 'lucide-react';
import { Toolbar } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import {
  Button,
  Badge,
  Skeleton,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  IconButton,
  Explainer,
} from '@vnetwork/ui/primitives';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { useTeamShares, useDeleteTeamShare, type TeamShare } from '@/api/teams';
import { AddPermissionDialog } from './components/AddPermissionDialog';
import { EditPermissionDialog } from './components/EditPermissionDialog';
import { actionLabel, formatDate } from './shared';

interface TeamPermissionsTabProps {
  teamId: string;
  isOwner: boolean;
  organization?: string;
}

export function TeamPermissionsTab({ teamId, isOwner, organization }: TeamPermissionsTabProps) {
  const { show } = useToast();
  const { data: shares = [], isLoading } = useTeamShares(teamId);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<TeamShare | null>(null);
  const [removing, setRemoving] = useState<TeamShare | null>(null);

  const remove = useDeleteTeamShare(teamId, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Permission removed' });
      setRemoving(null);
    },
    onError: () => show({ tone: 'danger', title: 'Could not remove permission' }),
  });

  const columns = useMemo<ColumnDef<TeamShare, unknown>[]>(() => {
    const cols: ColumnDef<TeamShare, unknown>[] = [
      {
        id: 'service',
        header: 'Service',
        accessorKey: 'service_name',
        cell: ({ row }) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)', fontWeight: 500 }}>
            <Shield size={14} color="var(--vn-fg-muted)" />
            {row.original.service_name ?? row.original.service_id}
          </span>
        ),
      },
      {
        id: 'actions-list',
        header: 'Permissions',
        enableSorting: false,
        cell: ({ row }) => (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-1)' }}>
            {row.original.actions.includes('all') ? (
              <Badge tone="primary" appearance="subtle" size="sm">
                Full Access
              </Badge>
            ) : (
              row.original.actions.map((a) => (
                <Badge key={a} tone="neutral" appearance="subtle" size="sm">
                  {actionLabel(a)}
                </Badge>
              ))
            )}
          </div>
        ),
      },
      {
        id: 'scope',
        header: 'Scope',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.project_id ? (
            <Badge tone="accent" appearance="outline" size="sm">
              Project
            </Badge>
          ) : (
            <Badge tone="info" appearance="outline" size="sm">
              Organization-wide
            </Badge>
          ),
      },
      {
        id: 'member',
        header: 'Applies to',
        enableSorting: false,
        cell: ({ row }) => (
          <span style={{ color: 'var(--vn-fg-muted)' }}>
            {row.original.member_email ?? (row.original.member_id ? 'Specific member' : 'All members')}
          </span>
        ),
      },
      {
        id: 'created',
        header: 'Created',
        accessorKey: 'created_at',
        size: 110,
        cell: ({ row }) => (
          <span style={{ color: 'var(--vn-fg-muted)' }}>{formatDate(row.original.created_at)}</span>
        ),
      },
    ];

    if (isOwner) {
      cols.push({
        id: 'row-actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div style={{ textAlign: 'right' }}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <IconButton variant="ghost" size="sm" aria-label="Permission actions">
                  <MoreHorizontal size={16} />
                </IconButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setEditing(row.original)}>
                  Edit permission
                </DropdownMenuItem>
                <DropdownMenuItem tone="danger" onSelect={() => setRemoving(row.original)}>
                  Remove permission
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      });
    }

    return cols;
  }, [isOwner]);

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Alert tone="info" icon={<Info size={16} />}>
        Permissions grant team members access to services. Organization-wide permissions apply
        across all projects; project-scoped permissions apply only within a specific project.
      </Alert>

      <Explainer
        title="How permissions work"
        description="How access is granted to members and resolved per request"
      >
        <ul
          style={{
            display: 'grid',
            gap: 'var(--vn-space-3)',
            margin: 0,
            paddingLeft: 'var(--vn-space-4)',
            fontSize: 'var(--vn-font-size-sm)',
            color: 'var(--vn-fg-muted)',
            lineHeight: 'var(--vn-line-height-normal)',
          }}
        >
          <li>
            Each permission grants <strong>one member</strong> (or all members) access to a service,
            optionally narrowed to a <strong>project</strong> and to <strong>specific domains</strong>.
          </li>
          <li>
            A member sees and manages <strong>only</strong> the resources granted to them
            (deny-by-default). The organization owner always sees everything.
          </li>
          <li style={{ color: 'var(--vn-warning-text)' }}>
            Heads-up: a whole-service grant <strong>overrides</strong> specific-domain grants for the
            same member — they will then see every domain. Avoid mixing the two for one member.
          </li>
          <li>
            Sharing several domains with a member creates <strong>one permission per domain</strong>.
            Editing a permission changes its actions only — to change the member, scope, or domain,
            remove it and add a new one.
          </li>
        </ul>
      </Explainer>

      {isOwner && (
        <Toolbar
          end={
            <Button
              variant="secondary"
              size="sm"
              startIcon={<Plus size={15} />}
              onClick={() => setAddOpen(true)}
            >
              Add permission
            </Button>
          }
        />
      )}

      {isLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 44 }} />
          ))}
        </div>
      ) : shares.length === 0 ? (
        <EmptyState
          icon={<Shield size={28} />}
          title="No permissions configured"
          description={
            isOwner
              ? 'Grant this team access to services to see permissions here.'
              : 'This team has no service permissions yet.'
          }
          actions={
            isOwner ? (
              <Button startIcon={<Plus size={15} />} onClick={() => setAddOpen(true)}>
                Add permission
              </Button>
            ) : undefined
          }
        />
      ) : (
        <DataTable columns={columns} data={shares} getRowId={(r) => r.id} />
      )}

      <AddPermissionDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        teamId={teamId}
        organization={organization}
      />
      <EditPermissionDialog
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        teamId={teamId}
        share={editing}
      />

      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        title="Remove permission?"
        description={`This revokes access to “${
          removing?.service_name ?? removing?.service_id ?? 'this service'
        }” for this team.`}
        confirmLabel="Remove"
        destructive
        loading={remove.isPending}
        onConfirm={() => removing && remove.mutate(removing.id)}
      />
    </div>
  );
}
